import React, { useState, useEffect } from 'react';
// 🔥 1. هنستورد أدوات الراوتر
import { 
  BrowserRouter as Router, 
  Routes, 
  Route, 
  Navigate,
  useSearchParams
} from 'react-router-dom';
import { onAuthStateChange, signInWithPassword, getSession, consumeMagicLinkToken } from './lib/supabaseAuth';
import { setEmbeddedFromUrl, setBrandNameFromUrl } from './lib/embedMode';

// 🔥 2. هنستورد كل الصفحات
import LoginPage from './LoginPage';
import { LegacyAgentRedirect } from './AgentPage';
import ClientDashboard from './ClientDashboard';
import SignupPage from './SignupPage'; // الصفحة الجديدة
import BillingPage from './BillingPage'; // صفحة الباقات
import ShopifyOnboardingPage from './ShopifyOnboardingPage'; // صفحة Shopify Onboarding
import KnowledgeBasePage from './KnowledgeBasePage'; // 📚 صفحة قاعدة المعرفة
import BostaPage from './BostaPage'; // 📦 صفحة إعدادات Bosta
import './App.css';
import { useI18n } from './i18n';

function App() {
  // � ALL hooks must be called FIRST - unconditionally (React rules)
  const [currentUser, setCurrentUser] = useState(null);
  const [loading, setLoading] = useState(true);
  const [autoLoginAttempted, setAutoLoginAttempted] = useState(false);
  const { t, lang, toggleLang } = useI18n();

  // 🔥 Auto-login with email & password if available
  useEffect(() => {
    const attemptAutoLogin = async () => {
      const email = localStorage.getItem('shopify_auto_email');
      const password = localStorage.getItem('shopify_auto_password');
      
      if (email && password && !autoLoginAttempted) {
        console.log('🔐 Shopify credentials found in localStorage - attempting auto-login...');
        setAutoLoginAttempted(true);
        
        try {
          // Replaces Firebase's signInWithEmailAndPassword(auth, email, password).
          // Kept for backward-compat: any browser that still has these
          // localStorage keys cached from BEFORE this Wave (ShopifyOnboardingPage.js
          // no longer writes them — it establishes a real GoTrue session
          // directly now, see that file's handleSubmit).
          const { data, error: authError } = await signInWithPassword(email, password);
          if (authError) throw authError;
          console.log('✅ Auto-login successful:', data?.user?.id);

          // مسح البيانات بعد الاستخدام
          localStorage.removeItem('shopify_auto_email');
          localStorage.removeItem('shopify_auto_password');
        } catch (error) {
          console.error('❌ Auto-login failed:', error);
          // مسح البيانات لو فشل
          localStorage.removeItem('shopify_auto_email');
          localStorage.removeItem('shopify_auto_password');
          localStorage.removeItem('shopify_auth_uid');
        }
      }
    };

    attemptAutoLogin();
  }, [autoLoginAttempted]);
  // 🔥 3. الكود بتاعك زي ما هو (ممتاز) — بس onAuthStateChanged(auth,...)
  // بقى onAuthStateChange(...) (GoTrue session بدل Firebase user).
  useEffect(() => {
    const unsubscribe = onAuthStateChange((user) => {
      setCurrentUser(user);
      setLoading(false);
    });
    return () => unsubscribe();
  }, []);
  
  // 🚨 AFTER hooks: Check for shop parameter to hijack render
  console.log('🔍 CHECKING URL PARAMETERS...');
  console.log('   Full URL:', window.location.href);
  console.log('   Pathname:', window.location.pathname);
  console.log('   Search string:', window.location.search);
  
  const params = new URLSearchParams(window.location.search);
  const shopParam = params.get('shop');
  const hostParam = params.get('host');
  const currentPath = window.location.pathname;

  // 🔗 Capture Daf One embed mode (?embedded=1) once on first load and persist
  // it for the tab — internal SPA navigation drops the query param. Used to
  // hide this dashboard's own payment UI while embedded inside Daf One.
  setEmbeddedFromUrl();
  // 🔗 Capture the Daf One brand name (?brandName=) once, same reason, so the
  // sidebar can show the real store name instead of the shop handle.
  setBrandNameFromUrl();

  console.log('   Parsed shop:', shopParam);
  console.log('   Parsed host:', hostParam);

  // 🔒 Wave 3b security fix (uid-in-URL): `?uid=`/`?admin=` are NEVER read
  // as an identity source anywhere in this file anymore (see
  // src/lib/MIGRATION_MAP.md §2 #3 + src/lib/supabaseAuth.js's header
  // comment). `shouldHijack` only decides whether to show the
  // "coming from Shopify, figure out onboarding-vs-dashboard" branch —
  // it no longer special-cases a `uid` param at all.
  const shouldHijack = shopParam &&
                       currentPath !== '/shopify-onboarding' &&
                       currentPath !== '/dashboard' &&
                       currentPath !== '/auth';
  
  // EARLY RETURN: If shop exists AND not on onboarding/dashboard page AND no uid, skip ALL auth logic
  if (shouldHijack) {
    console.log('🚨🚨🚨 HIJACK ACTIVATED: Shop parameter detected!');
    console.log('   Shop:', shopParam);
    console.log('   Host:', hostParam);
    console.log('   → BYPASSING: Auth check, loading state, login page');
    console.log('   → RENDERING: ShopifyDashboardHandler component');
    
    // Return Shopify handler immediately (but AFTER all hooks)
    // 🔧 FIX: must render inside a <Router> so <Navigate> (used inside
    // ShopifyDashboardHandler) has a valid router context. Without this,
    // <Navigate> throws "may be used only in the context of a <Router>"
    // and the page goes blank. This is a standalone Router just for this
    // early-return branch — it does not affect the main <Router><Routes>
    // tree below (e.g. /dashboard?shop= keeps working exactly as before).
    return (
      <Router>
        <ShopifyDashboardHandler shop={shopParam} host={hostParam} />
      </Router>
    );
  }
  
  if (shopParam && currentPath === '/shopify-onboarding') {
    console.log('✅ On onboarding page - letting Router handle it normally');
  } else if (shopParam && currentPath === '/dashboard') {
    console.log('✅ Shop + /dashboard - letting Router/session handle it normally');
  } else {
    console.log('❌ NO shop parameter found - continuing with normal auth flow');
  }

  // 🔥 5. كود التحميل زي ما هو (ممتاز)
  if (loading) {
    return (
      <div style={{
        display: 'flex',
        flexDirection: 'column',
        alignItems: 'center',
        justifyContent: 'center',
        height: '100vh',
        gap: '20px',
        background: '#f4f3fb',
        color: '#1b1f2a',
        fontFamily: "'IBM Plex Sans Arabic', sans-serif"
      }}>
        <button
          type="button"
          onClick={toggleLang}
          style={{
            background: '#ffffff',
            color: '#6c5ce7',
            border: '1px solid #eceef3',
            borderRadius: '10px',
            padding: '8px 14px',
            cursor: 'pointer',
            fontWeight: 700,
            display: 'flex',
            alignItems: 'center',
            gap: '6px'
          }}
          aria-label={t('lang.toggle')}
        >
          <span aria-hidden="true">🌐</span>
          <span>{lang === 'en' ? 'EN' : 'AR'}</span>
        </button>
        <div style={{
          width: '44px', height: '44px', borderRadius: '50%',
          border: '4px solid #e7e3fb', borderTopColor: '#6c5ce7',
          animation: 'rdspin 0.8s linear infinite'
        }} />
        <style>{`@keyframes rdspin{to{transform:rotate(360deg)}}`}</style>
        <div style={{ fontSize: '20px', fontWeight: 600 }}>{t('loading.app')}</div>
      </div>
    );
  }

  // 🔥 6. ده الراوتر الجديد (بديل الـ if القديمة)
  return (
    <Router>
      <Routes>
        {/* --- المسارات العامة (للي مش مسجل دخول) --- */}
        
        <Route
          path="/"
          element={<HomeRoute currentUser={currentUser} />}
        />
        <Route
          path="/signup"
          element={
            !currentUser ? <SignupPage /> : <Navigate to="/dashboard" replace />
          }
        />

        {/* 🔐 Seamless-login (magic-link) exchange — replaces the old
            `?uid=`/`?admin=` redirect. Public route on purpose: the token
            itself is what proves identity (single-use, minted server-side
            only after the caller was already verified — Shopify OAuth
            session or a super-admin's impersonate call). See
            MagicLinkHandler below + src/lib/supabaseAuth.js's
            consumeMagicLinkToken(). */}
        <Route path="/auth" element={<MagicLinkHandler />} />

        {/* --- المسارات المحمية (للي مسجل دخول) --- */}
        
        <Route 
          path="/dashboard" 
          element={<DashboardWrapperWithAuth currentUser={currentUser} />} 
        />

        {/* صفحة الباقات */}
        <Route 
          path="/billing" 
          element={<BillingPage />} 
        />

        {/* صفحة Shopify Onboarding */}
        <Route 
          path="/shopify-onboarding" 
          element={<ShopifyOnboardingPage />} 
        />

        {/* 📚 صفحة قاعدة المعرفة */}
        <Route 
          path="/knowledge-base" 
          element={<KnowledgeBasePage />} 
        />

        {/* 📦 صفحة إعدادات Bosta */}
        <Route 
          path="/bosta" 
          element={<BostaPage />} 
        />

        {/* لو كتب أي رابط غلط، رجعه للبداية */}
        <Route path="/agent" element={currentUser ? <LegacyAgentRedirect /> : <Navigate to="/login" replace />} />
        <Route path="*" element={<Navigate to="/" replace />} />
        
      </Routes>
    </Router>
  );
}

function safeDashboardTarget(rawTarget) {
  if (!rawTarget) return '/dashboard';
  try {
    const parsed = new URL(rawTarget, window.location.origin);
    if (parsed.origin !== window.location.origin || parsed.pathname !== '/dashboard') {
      return '/dashboard';
    }
    return `${parsed.pathname}${parsed.search}`;
  } catch {
    return '/dashboard';
  }
}

function HomeRoute({ currentUser }) {
  const [searchParams] = useSearchParams();
  if (!currentUser) return <LoginPage />;
  return <Navigate to={safeDashboardTarget(searchParams.get('next'))} replace />;
}

// Wrapper يتحقق لو مسجل دخول ولا لأ
// 🔒 Wave 3b security fix: شيلنا `?admin=`/`?uid=` تمامًا كمصدر صلاحية.
// أي زائر مش مسجّل دخول (بغض النظر عن أي query param) بيترحّل لصفحة اللوجن
// — "عرض كأي براند" (super-admin "view as") محتاج تصميم جديد (RPC مخصّصة
// SECURITY DEFINER تتحقق من isSuperAdmin() أولًا) لسه مش موجود، شوف
// supabaseAuth.js's "Known gaps" #1.
function DashboardWrapperWithAuth({ currentUser }) {
  const [searchParams] = useSearchParams();
  const shopParam = searchParams.get('shop');
  const platformParam = searchParams.get('platform');

  if (!currentUser) {
    // لو جاي من Shopify بلا جلسة (لسه مش onboarded أو الجلسة انتهت)،
    // نوجّهه لصفحة الـonboarding بدل صفحة لوجن عمياء بلا سياق — نفس
    // fallback بتاع ShopifyDashboardHandler تحت. أي حالة تانية → لوجن عادي.
    if (shopParam && platformParam === 'shopify') {
      return <Navigate to={`/shopify-onboarding?shop=${encodeURIComponent(shopParam)}&platform=shopify`} replace />;
    }
    const next = `/dashboard${window.location.search || ''}`;
    return <Navigate to={`/?next=${encodeURIComponent(next)}`} replace />;
  }

  return <DashboardWrapper user={currentUser} />;
}

// 🔐 Seamless-login (magic-link) exchange.
// Replaces the old `?uid=<auth_uid>`/`?admin=&uid=` redirects everywhere
// they used to appear (Shopify auto-login after OAuth, super-admin
// "view as brand"). The server (Shopify app / super-admin impersonate
// endpoint) mints a single-use GoTrue `hashed_token` ONLY after it has
// already verified the caller is entitled to that login (Shopify OAuth
// session / an authenticated super-admin), then redirects the browser here
// with `?token=...`. This component's ONLY job is to exchange that token
// for a real GoTrue session via `consumeMagicLinkToken()` — it never reads
// any other identity signal from the URL. Any failure (missing/expired/
// already-used token, network error) falls back to the normal login page —
// never to a "logged in with no brand" limbo state.
function MagicLinkHandler() {
  const [status, setStatus] = useState('verifying'); // 'verifying' | 'done' | 'failed'
  const { t, lang, toggleLang } = useI18n();

  useEffect(() => {
    let cancelled = false;

    async function run() {
      const params = new URLSearchParams(window.location.search);
      const token = params.get('token');

      if (!token) {
        if (!cancelled) setStatus('failed');
        return;
      }

      const { session, error } = await consumeMagicLinkToken(token);

      // Always scrub the single-use token out of the URL/browser history
      // immediately, regardless of outcome — it must never sit around in
      // history/referrer headers after this point.
      window.history.replaceState({}, '', '/auth');

      if (cancelled) return;

      if (error || !session) {
        console.error('❌ Magic-link exchange failed:', error?.message);
        setStatus('failed');
        return;
      }

      setStatus('done');
    }

    run();
    return () => { cancelled = true; };
  }, []);

  if (status === 'failed') {
    // Fail-safe: plain login page, never a broken/half-authenticated view.
    return <Navigate to="/" replace />;
  }

  if (status === 'done') {
    return <Navigate to="/dashboard" replace />;
  }

  return (
    <div style={{
      display: 'flex',
      flexDirection: 'column',
      alignItems: 'center',
      justifyContent: 'center',
      height: '100vh',
      gap: '20px',
      background: '#f4f3fb',
      color: '#1b1f2a',
      fontFamily: "'IBM Plex Sans Arabic', sans-serif"
    }}>
      <button
        type="button"
        onClick={toggleLang}
        style={{
          background: '#ffffff',
          color: '#6c5ce7',
          border: '1px solid #eceef3',
          borderRadius: '10px',
          padding: '8px 14px',
          cursor: 'pointer',
          fontWeight: 700,
          display: 'flex',
          alignItems: 'center',
          gap: '6px'
        }}
        aria-label={t('lang.toggle')}
      >
        <span aria-hidden="true">🌐</span>
        <span>{lang === 'en' ? 'EN' : 'AR'}</span>
      </button>
      <div style={{
        width: '44px', height: '44px', borderRadius: '50%',
        border: '4px solid #e7e3fb', borderTopColor: '#6c5ce7',
        animation: 'rdspin 0.8s linear infinite'
      }} />
      <style>{`@keyframes rdspin{to{transform:rotate(360deg)}}`}</style>
      <div style={{ fontSize: '20px', fontWeight: 600 }}>{t('loading.magicLink')}</div>
    </div>
  );
}

// 🔥 Handler خاص بـ Shopify clients
// 🔒 Wave 3b security fix: شيلنا الاستعلام الغير مُوثَّق (`where('shopify_domain',...)`
// بلا أي جلسة) + الـ `Navigate to /dashboard?uid=...` اللي كان بيدّي دخول
// كامل لأي حد يعرف/يخمّن دومين Shopify. البديل: نتأكد بس هل فيه جلسة GoTrue
// شغّالة فعلًا (من onboarding سابق في نفس المتصفح) — لو فيه، ندخل الداشبورد
// عادي (getMyBrand() هيحل البراند من الجلسة). لو مفيش، نوجّه لصفحة
// onboarding (اللي بترفض بأمان لو الشوب onboarded خلاص فعليًا، وتقول
// "سجّل دخول" — انحراف مقصود موثّق في supabaseAuth.js "Known gaps" #2:
// مفيش طريقة آمنة نفرّق بها "onboarded بس من غير جلسة" عن "لسه ما اتعملوش
// onboarding" غير جلسة حقيقية بعد OAuth، وده برّه نطاق هذه المهمة).
function ShopifyDashboardHandler({ shop, host }) {
  console.log('🎯 ShopifyDashboardHandler MOUNTED');
  console.log('   Received shop:', shop);
  console.log('   Received host:', host);

  const [loading, setLoading] = useState(true);
  const [hasSession, setHasSession] = useState(false);
  const { t, lang, toggleLang } = useI18n();

  useEffect(() => {
    let cancelled = false;
    getSession().then(({ session }) => {
      if (cancelled) return;
      setHasSession(!!session);
      setLoading(false);
    });
    return () => { cancelled = true; };
  }, [shop]);

  if (loading) {
    return (
      <div style={{ 
        display: 'flex', 
        flexDirection: 'column',
        alignItems: 'center', 
        justifyContent: 'center', 
        height: '100vh',
        background: '#f4f3fb',
        color: '#1b1f2a',
        fontSize: '20px',
        gap: '20px',
        fontFamily: "'IBM Plex Sans Arabic', sans-serif"
      }}>
        <button
          type="button"
          onClick={toggleLang}
          style={{
            background: '#ffffff',
            color: '#6c5ce7',
            border: '1px solid #eceef3',
            borderRadius: '10px',
            padding: '8px 14px',
            cursor: 'pointer',
            fontWeight: 700,
            display: 'flex',
            alignItems: 'center',
            gap: '6px'
          }}
          aria-label={t('lang.toggle')}
        >
          <span aria-hidden="true">🌐</span>
          <span>{lang === 'en' ? 'EN' : 'AR'}</span>
        </button>
        <div style={{
          width: '44px', height: '44px', borderRadius: '50%',
          border: '4px solid #e7e3fb', borderTopColor: '#6c5ce7',
          animation: 'rdspin 0.8s linear infinite'
        }} />
        <style>{`@keyframes rdspin{to{transform:rotate(360deg)}}`}</style>
        <span style={{ fontWeight: 600 }}>{t('loading.shopifyCheck')}</span>
      </div>
    );
  }

  if (hasSession) {
    // ✅ فيه جلسة GoTrue حقيقية شغّالة بالفعل (من onboarding سابق في نفس
    // المتصفح) — ندخل الداشبورد عادي، بلا أي uid في الرابط. `getMyBrand()`
    // (داخل ClientDashboard.js) هو اللي بيحل البراند من الجلسة.
    return <Navigate to="/dashboard" replace />;
  }

  // مفيش جلسة — نوجّه للـonboarding (بيرفض بأمان لو الشوب onboarded خلاص،
  // شوف تعليق الدالة فوق).
  return <Navigate to={`/shopify-onboarding?shop=${encodeURIComponent(shop)}&platform=shopify`} replace />;
}

// Wrapper للـ Dashboard
// 🔒 Wave 3b security fix: مفيش أي props هوية بتتقرأ من الـURL هنا (شيلنا
// viewingClientUid/isSuperAdminView/shopifyShop/platform بالكامل) —
// ClientDashboard.js بيحل هويته الوحيدة عبر getMyBrand()/الجلسة (شوف
// subscribeMyBrand في src/lib/confirmationData.js).
function DashboardWrapper({ user }) {
  console.log('🔍 DashboardWrapper Props:');
  console.log('  - user:', user);

  return <ClientDashboard user={user} />;
}

export default App;
